import {AutoComplete, DatePicker, Form, Input, InputNumber, Select} from "antd";
import {FormInstance} from "antd/lib/form/Form";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import type {InputRef} from "antd";
import {FIELD_TYPE, FORMAT_TIME, LOAI_PHIEU_KHO} from "types/enums";
import dayjs from "dayjs";
import {useAppSelector} from "redux/hooks";
import {pharmacyStoreService, catalogService, storeService} from "api";
import moment from "moment";
import {
  formatToCurrencyTypeToFixed,
  isValuableString,
  randomId,
  toNumber,
} from "helper/common";

import {Rule} from "antd/es/form";

type EditableRowProps = {
  index: number;
};
const {INPUT, SELECT, NUMBER, DATE, AUTO_COMPLETE, COMBO_BOX, CURRENCY, CODE} =
  FIELD_TYPE;
export type EditableCellProps = {
  title: React.ReactNode;
  editable?: boolean;
  children: React.ReactNode;
  dataIndex: any;
  record: any;
  selected: boolean;
  options: any;
  name: string;
  index: number;
  type: FIELD_TYPE;
  disabled?: boolean;
  value?: any;
  onChange?: (e) => void;
  handleSave: (record, index) => void;
  onFocus?: () => void;
  onPressEnter?: (e) => void;
  addonAfter?: any;
  initRules?: Rule[];
  hidden?: boolean;
  autoFocus?: boolean;
  form?: any;
  setTongTien?: any;
  placeholder?: string;
};

export const EditableContext = React.createContext<FormInstance<any> | null>(
  null
);

export const EditableRow: React.FC<EditableRowProps> = ({index, ...props}) => {
  return (
    <>
      <tr {...props} />
    </>
  );
};

export const EditableCell: React.FC<EditableCellProps> = ({
  title,
  editable,
  children,
  dataIndex,
  record,
  selected,
  options,
  handleSave,
  name,
  index,
  type,
  onChange,
  onPressEnter,
  disabled,
  value,
  hidden,
  form,
  autoFocus,
  initRules,
  setTongTien,
  placeholder,
  ...restProps
}) => {
  const [editing, setEditing] = useState(false);
  const inputRef = useRef<InputRef>(null);
  const [newOptions, setNewOptions] = useState(options);
  const [max, setMax] = useState(null);
  const [rules, setRules] = useState<Rule[]>(initRules);
  const {listDongNganSach, listNguonThanhToan, listDuAn} = useAppSelector(
    state => state.proposeShopping
  );
  const [openDropdown, setOpenDropdown] = useState<boolean>(false);
  const setFieldValue = (dataIndex: string, value: any) => {
    form?.setFieldValue([name, index, dataIndex], value);
  };
  const getFieldValue = (dataIndex: string) => {
    return form?.getFieldValue([name, index, dataIndex]);
  };
  const setThanhTien = () => {
    const donGia = toNumber(getFieldValue("donGia"));
    const soLuong = toNumber(getFieldValue("soLuong"));
    const vat = toNumber(getFieldValue("vat"));
    const thanhTien = (soLuong * donGia * (100 + vat)) / 100;
    setFieldValue("thanhTien", thanhTien);
  };
  useEffect(() => {
    inputRef.current?.focus();
  }, [editing]);

  let childNode = children;
  useEffect(() => {
    const _setOptions = listDongNganSach => {
      const _options = listDongNganSach.map(e => ({
        label: e.name,
        value: e.name,
        key: randomId(),
      }));
      setNewOptions(_options);
    };
    if (dataIndex == "nganSach") {
      _setOptions(listDongNganSach);
    }
  }, [listDongNganSach]);
  useEffect(() => {
    const _setOptions = listNguonThanhToan => {
      const _options = listNguonThanhToan.map(e => ({
        label: e.name,
        value: e.name,
        key: randomId(),
      }));
      setNewOptions(_options);
    };
    if (dataIndex == "nguonThanhToan") {
      _setOptions(listNguonThanhToan);
    }
  }, [listNguonThanhToan]);
  useEffect(() => {
    const _setOptions = listDuAn => {
      const _options = listDuAn.map(e => ({
        label: e.name,
        value: e.name,
        key: randomId(),
      }));
      setNewOptions(_options);
    };
    if (dataIndex == "duAn") {
      _setOptions(listDuAn);
    }
  }, [listDuAn]);
  if (type == INPUT) {
    childNode = (
      <Form.Item
        name={[name, index, dataIndex]}
        initialValue={getFieldValue(dataIndex) ?? null}
        rules={rules}
      >
        <Input
          placeholder={disabled ? "--" : "Nhập"}
          disabled={disabled}
          onClick={() => {}}
          onChange={async e => {}}
        ></Input>
      </Form.Item>
    );
  }
  if (type == DATE) {
    childNode = (
      <Form.Item
        name={[name, index, dataIndex]}
        initialValue={null}
        rules={rules}
      >
        <DatePicker
          disabled={disabled}
          onChange={() => {}}
          format={FORMAT_TIME.dayFull.toString()}
        ></DatePicker>
      </Form.Item>
    );
  }
  if (type == NUMBER) {
    childNode = (
      <Form.Item
        name={[name, index, dataIndex]}
        initialValue={getFieldValue(dataIndex) ?? null}
        rules={rules}
      >
        <InputNumber
          disabled={disabled}
          onChange={e => {
            if (dataIndex == "soLuong" || dataIndex == "vat") {
              setThanhTien();
              setTongTien();
            }
          }}
          onClick={e => {}}
          placeholder={disabled ? "--" : "Nhập"}
          max={max}
          min={0}
          controls={false}
          {...restProps}
        ></InputNumber>
      </Form.Item>
    );
  }
  if (type == AUTO_COMPLETE) {
    childNode = (
      <Form.Item
        name={[name, index, dataIndex]}
        initialValue={getFieldValue(dataIndex) ?? null}
        rules={rules}
      >
        <AutoComplete
          showSearch
          autoFocus={autoFocus}
          allowClear
          placeholder={disabled ? "--" : "Chọn"}
          notFoundContent={null}
          value={value}
          defaultActiveFirstOption={false}
          filterOption={(inputValue, option) =>
            option?.label
              ?.toString()
              .toUpperCase()
              .indexOf(inputValue.toUpperCase()) !== -1
          }
          open={openDropdown}
          onFocus={() => {
            setOpenDropdown(true);
          }}
          onBlur={() => {
            setOpenDropdown(false);
          }}
          onSelect={() => {
            setOpenDropdown(false);
          }}
          onClick={async e => {}}
          options={newOptions}
          disabled={disabled}
          onClear={() => {}}
          onChange={async e => {
            // lay list so lo
          }}
          {...restProps}
        ></AutoComplete>
      </Form.Item>
    );
  }
  if (type == COMBO_BOX) {
    childNode = (
      <Form.Item
        style={{margin: 0}}
        name={[name, index, dataIndex]}
        initialValue={getFieldValue(dataIndex) ?? null}
        rules={rules}
      >
        <Select
          showSearch
          style={{
            width: "100%",
          }}
          filterOption={(input, option) =>
            (option?.label.toString().toLowerCase() ?? "").includes(
              input.toLowerCase()
            )
          }
          open={openDropdown}
          onFocus={() => {
            setOpenDropdown(true);
          }}
          onBlur={() => {
            setOpenDropdown(false);
          }}
          onSelect={() => {
            setOpenDropdown(false);
          }}
          placeholder={disabled ? "--" : "Chọn"}
          options={newOptions}
          disabled={disabled}
          allowClear={true}
          onChange={e => {}}
          onClick={async e => {}}
          {...restProps}
        />
      </Form.Item>
    );
  }

  if (type == CURRENCY) {
    childNode = (
      <Form.Item
        name={[name, index, dataIndex]}
        initialValue={getFieldValue(dataIndex) ?? null}
        rules={rules}
      >
        <InputNumber
          disabled={disabled}
          defaultValue={value}
          formatter={value => formatToCurrencyTypeToFixed(value, 0)}
          parser={value => value!.replace(/\$\s?|(,*)/g, "")}
          onChange={e => {
            if (dataIndex == "donGia") {
              setThanhTien();
              setTongTien();
            }
            if (dataIndex == "thanhTien") {
              setTongTien();
            }
          }}
          placeholder={!disabled ? "Nhập" : ""}
          addonAfter="VNĐ"
          stringMode
          controls={false}
          min={0}
          {...restProps}
        />
      </Form.Item>
    );
  }

  return <td {...restProps}>{childNode}</td>;
};
